// Used in makequest-iframe, composite-assignment-submit, singular-assignment-stimulus-activity-submit

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { FileItem, FileUploader, Headers } from "ng2-file-upload";

// RXJS/Store
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { CookieService } from "#services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
import { AssignmentUploadService } from "@carnegie-learning/data-access-angular-passport";
import { CompositeTaskAssignment } from "cli-types";

@Component({
    selector: "app-assignment-uploader",
    templateUrl: "./assignment-uploader.component.html",
    styleUrls: ["./assignment-uploader.component.scss"],
    standalone: false
})
export class AssignmentUploaderComponent implements OnInit, OnDestroy {
    private _taskAssignment =
        new BehaviorSubject<CompositeTaskAssignment | null>(null);
    taskAssignment: CompositeTaskAssignment;
    isUploadingFile = true;
    isUploadingUrl = false;
    activeButtonFile = true;
    activeButtonUrl = false;
    urlToUpload: string | null;
    showUrlProgressBar: boolean;
    showFileProgressBar: boolean;
    reg = "(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?";
    unsupportedFileType = false;

    uploadUrlForm = this.fb.group({
        url: ["", [this.formHelper.isValidUrl]],
    });

    supportedFileTypes = [
        ".docx",
        ".doc",
        ".dot",
        ".docm",
        ".dotx",
        ".dotm",
        ".xls",
        ".xlsx",
        ".xlsm",
        ".xltx",
        ".xltm",
        ".ppt",
        ".pptx",
        ".pptm",
        ".potx",
        ".potm",
        ".ppsx",
        ".ppsm",
        ".txt",
        ".pdf",
        ".jpg",
        ".png",
        ".bmp",
        ".gif",
        ".jpeg",
        ".mp4",
        ".mov",
        ".flv",
        ".webm",
        ".mp3",
        ".zip",
        ".max",
        ".gmx",
        ".gmz",
        ".unity3d",
        ".gameproj",
    ];

    supportedFileTypeOnlyImages = [
        ".jpg",
        ".png",
        ".bmp",
        ".gif",
        ".jpeg",
        ".svg",
        ".tiff",
    ];

    imageMimeTypes = [
        "image/jpg",
        "image/png",
        "image/bmp",
        "image/gif",
        "image/jpeg",
        "image/svg+xml",
        "image/tiff",
    ];

    private ngUnsubscribe = new Subject();

    @Input()
    set taskAssignmentData(value) {
        // set the latest value for _taskAssignment BehaviorSubject
        this._taskAssignment.next(value);
    }
    get taskAssignmentData() {
        // get the latest value from _taskAssignment BehaviorSubject
        return this._taskAssignment.getValue();
    }

    @Input() apiUrl: string;
    @Input() hideUrlForm: boolean;
    @Input() imagesOnly: boolean;

    @Output() uploadSuccess = new EventEmitter<any>();
    @Output() fileSubmissionSet = new EventEmitter<boolean>();

    // File Upload
    authToken = this.cookieService.get("CL_TOKEN2");
    header: Headers = { name: "authorization", value: this.authToken };
    public uploader: FileUploader;
    public hasBaseDropZoneOver = false;

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    constructor(
        public localStorage: LocalStorage,
        public assignmentUploadService: AssignmentUploadService,
        private cookieService: CookieService,
        private fb: UntypedFormBuilder,
        private formHelper: FormHelper
    ) {
        this.uploader = new FileUploader({
            isHTML5: true,
            maxFileSize: 64 * 1024 * 1024, // 64 MB
            url: this.apiUrl,
            authToken: this.authToken,
            autoUpload: false,
            method: "POST",
        });
        if (this.uploader) {
            this.uploader.onAfterAddingFile = (item) => {
                item.withCredentials = false;
                // check if supported file type
                if (this.checkFileType(item)) {
                    if (this.uploader.queue.length > 1) {
                        this.uploader.removeFromQueue(this.uploader.queue[0]);
                    }
                    this.unsupportedFileType = false;
                    this.fileSubmissionSet.emit(true);
                } else {
                    this.unsupportedFileType = true;
                    this.uploader.clearQueue();
                    this.fileSubmissionSet.emit(false);
                }
            };

            this.uploader.onSuccessItem = (
                item: any,
                response: any,
                status: any,
                headers: any
            ) => {
                if (item.isSuccess) {
                    // console.log(response);
                    // uploaded successfully
                    this.uploadSuccess.emit(JSON.parse(response));
                    this.showFileProgressBar = false;
                } else {
                    // upload failed
                    this.showFileProgressBar = false;
                }
            };

            this.uploader.onErrorItem = (
                item: any,
                response: any,
                status: any,
                header: any
            ) => {
                this.showFileProgressBar = false;
            };
        }
    }

    checkFileType(file: FileItem) {
        const fileName = file.file.name;
        let sft = this.supportedFileTypes;
        if (this.imagesOnly) {
            sft = this.supportedFileTypeOnlyImages;
        }
        const validFileType = sft.some((type) => {
            return fileName?.includes(type);
        });
        if (validFileType) {
            return true;
        }
    }

    ngOnInit() {
        this.urlToUpload = null;
        this._taskAssignment.subscribe((taskAssignment) => {
            if (taskAssignment) {
                this.taskAssignment = taskAssignment;
            }
            this.uploader.options.url = this.apiUrl;
            if (this.imagesOnly) {
                this.uploader.options.allowedMimeType = this.imageMimeTypes;
            }
            if (this.taskAssignment?.submissionFilePath) {
                this.activeButtonFile = true;
                this.activeButtonUrl = false;
                this.isUploadingFile = true;
                this.isUploadingUrl = false;
            } else if (this.taskAssignment?.submissionUrl) {
                this.activeButtonFile = false;
                this.activeButtonUrl = true;
                this.isUploadingFile = false;
                this.isUploadingUrl = true;
                this.urlToUpload = this.taskAssignment.submissionUrl;
            }
        });
    }

    selectFileType() {
        this.isUploadingFile = true;
        this.isUploadingUrl = false;
        this.activeButtonFile = true;
        this.activeButtonUrl = false;
        this.fileSubmissionSet.emit(
            !!this.uploader.queue.length || !!this.urlToUpload
        );
    }

    selectUrlType() {
        this.isUploadingFile = false;
        this.isUploadingUrl = true;
        this.activeButtonFile = false;
        this.activeButtonUrl = true;
        this.fileSubmissionSet.emit(
            !!this.uploader.queue.length || !!this.urlToUpload
        );
    }

    uploadAssignment() {
        // PAS-9828 we are not limiting to only submitting File or URL, if student insert URL and a file, both should be uploaded.

        if (this.urlToUpload) {
            this.showUrlProgressBar = true;
            const urlToUpload = { url: this.urlToUpload, completed: true };
            this.assignmentUploadService
                .uploadUrl(urlToUpload, this.apiUrl)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((response) => {
                    this.showUrlProgressBar = false;
                    if (this.uploader?.queue[0]) {
                        this.uploadFile();
                    } else {
                        this.uploadSuccess.emit(response);
                    }
                });
        } else if (this.uploader?.queue[0]) {
            this.uploadFile();
        }
    }

    uploadFile() {
        this.showFileProgressBar = true;
        this.uploader.queue[0].upload();
    }

    setUrlSubmission(url: string | null) {
        if (url && this.uploadUrlForm.controls.url.valid) {
            this.fileSubmissionSet.emit(true);
        } else {
            this.fileSubmissionSet.emit(false);
        }
    }

    clearSubmissionSet() {
        this.uploader.queue[0].remove();
        this.fileSubmissionSet.emit(false);
    }

    public onChange(event: any): void {
        event.target.value = "";
    }

    fileSelected() {
        // console.log(this.uploader.queue);
        if (this.uploader.queue.length > 0) {
            // If the Queue is greater than 1, remove the first one.
            if (this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }

            this.showFileProgressBar = false;
        }
    }

    getMimeTypes() {
        if (this.imagesOnly) {
            return this.imageMimeTypes.join(",");
        }
        return "";
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
